import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { MapPinPlus, X } from "lucide-react";
import type { ItemKind, ItemStatus, ItineraryItemDTO, QuickAddPlace, QuickAddResult, TransportMode } from "@shared/types";
import { ITEM_KINDS, ITEM_STATUSES, TRANSPORT_MODES } from "@shared/types";
import { t, tj } from "@/i18n";
import { fmtDayShort, isoToLocalInput, localInputToIso, weekday } from "@/lib/format";
import { ITEM_KIND, ITEM_STATUS, MODE } from "@/lib/meta";
import { toast, useUI } from "@/lib/store";
import { tripApi, type Bundle, type Derived } from "@/lib/trip";
import { Avatar, Button, cx, Field, Input, Modal, Select, Textarea } from "../ui";
import { SmartFill } from "../SmartFill";
import { PlaceSelect } from "./PlaceSelect";

type Draft = Partial<ItineraryItemDTO> & { kind: ItemKind; day: string };

export function ItemEditor({
  b,
  d,
  open,
  onClose,
  item,
  preset,
}: {
  b: Bundle;
  d: Derived;
  open: boolean;
  onClose: () => void;
  item?: ItineraryItemDTO | null;
  preset?: Draft | null;
}) {
  const [v, setV] = useState<Draft>(() => (item ? { ...item } : (preset ?? { kind: "visit", day: b.trip.startDate })));
  const [busy, setBusy] = useState(false);
  /** новое место из «умного добавления» — создаётся при сохранении */
  const [newPlace, setNewPlace] = useState<QuickAddPlace | null>(null);
  const tripId = b.trip.id;
  useEffect(() => {
    if (open) setV(item ? { ...item } : (preset ?? { kind: "visit", day: b.trip.startDate }));
    if (open) setNewPlace(null);
  }, [open, item, preset, b.trip.startDate]);

  const onSmart = (r: QuickAddResult) => {
    const it = r.item;
    const p = r.place;
    if (it) {
      const clean = Object.fromEntries(Object.entries(it).filter(([, x]) => x != null)) as Partial<ItineraryItemDTO>;
      setV((x) => ({ ...x, ...clean, kind: it.kind, day: it.day ?? x.day, status: it.status ?? x.status ?? "planned" }));
    }
    if (p?.existingPlaceId) {
      setNewPlace(null);
      setV((x) => ({ ...x, placeId: p.existingPlaceId, title: x.title || p.name }));
    } else if (p && (!it || it.kind !== "transport")) {
      setNewPlace(p);
      setV((x) => ({ ...x, placeId: null, kind: it?.kind ?? (p.category === "hotel" ? "stay" : "visit"), title: it?.title ?? x.title ?? p.name }));
      if (p.lat != null && p.lng != null) useUI.getState().focusMap({ center: [p.lat, p.lng], zoom: 15 });
    }
  };
  const up = (p: Partial<Draft>) => setV((x) => ({ ...x, ...p }));
  const canEdit = b.trip.myRole !== "viewer";

  const save = async () => {
    if (!v.title?.trim()) {
      const p = v.placeId ? d.places.get(v.placeId) : null;
      if (p) v.title = p.name;
      else if (v.kind === "transport" && v.fromName && v.toName) v.title = `${v.fromName} → ${v.toName}`;
      else return toast(t("Добавьте название"), { tone: "error" });
    }
    setBusy(true);
    let placeId = v.placeId ?? null;
    if (!placeId && newPlace && v.kind !== "transport" && v.kind !== "note") {
      if (newPlace.lat == null || newPlace.lng == null) {
        setBusy(false);
        return toast(t("У нового места нет координат — уточните описание или добавьте место на карте"), { tone: "error" });
      }
      try {
        const created = await tripApi.createPlace(tripId, {
          name: newPlace.name,
          nameLocal: newPlace.nameLocal,
          lat: newPlace.lat,
          lng: newPlace.lng,
          category: newPlace.category,
          status: "want",
          region: newPlace.region,
          address: newPlace.address,
          description: newPlace.description,
          url: newPlace.url,
          phone: newPlace.phone,
        });
        placeId = created.id;
      } catch (e) {
        setBusy(false);
        return toast((e as Error).message, { tone: "error" });
      }
    }
    const body: Partial<ItineraryItemDTO> = {
      day: v.day,
      kind: v.kind,
      status: v.status ?? "planned",
      placeId,
      title: v.title!.trim(),
      startTime: v.startTime || null,
      endTime: v.endTime || null,
      durationMin: v.durationMin ?? null,
      nights: v.kind === "stay" ? (v.nights ?? 1) : null,
      notes: v.notes || null,
      cost: v.cost ?? null,
      currency: v.cost != null ? (v.currency ?? "EUR") : null,
      bookingRef: v.bookingRef || null,
      bookingUrl: v.bookingUrl || null,
      contact: v.contact || null,
      participants: v.participants ?? [],
      mode: v.kind === "transport" ? (v.mode ?? "bus") : null,
      fromName: v.kind === "transport" ? (v.fromName ?? null) : null,
      fromLat: v.kind === "transport" ? (v.fromLat ?? null) : null,
      fromLng: v.kind === "transport" ? (v.fromLng ?? null) : null,
      toName: v.kind === "transport" ? (v.toName ?? null) : null,
      toLat: v.kind === "transport" ? (v.toLat ?? null) : null,
      toLng: v.kind === "transport" ? (v.toLng ?? null) : null,
      departAt: v.kind === "transport" ? (v.departAt ?? null) : null,
      arriveAt: v.kind === "transport" ? (v.arriveAt ?? null) : null,
      fromTz: v.kind === "transport" ? (v.fromTz ?? b.trip.destTz) : null,
      toTz: v.kind === "transport" ? (v.toTz ?? b.trip.destTz) : null,
      carrier: v.carrier || null,
    };
    try {
      if (item) await tripApi.updateItem(tripId, item.id, body);
      else await tripApi.createItem(tripId, body);
      onClose();
    } catch (e) {
      toast((e as Error).message, { tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const r = await tripApi.deleteItem(tripId, item.id);
    onClose();
    toast(t("Пункт удалён"), { action: r.activityId ? { label: t("Вернуть"), run: () => void tripApi.undo(tripId, { activityId: r.activityId! }) } : undefined });
  };

  const placesArr = b.places;
  const fromTz = v.fromTz ?? b.trip.destTz;
  const toTz = v.toTz ?? b.trip.destTz;
  const allDays = [...new Set([...d.days.map((x) => x.day), v.day])].sort();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={item ? t("Пункт плана") : t("Новый пункт плана")}
      footer={
        canEdit && (
          <>
            {item && (
              <Button variant="ghost" className="mr-auto text-[var(--danger)]" icon={<Trash2 size={16} />} onClick={remove}>
                {t("Удалить")}
              </Button>
            )}
            <Button onClick={onClose}>{t("Отмена")}</Button>
            <Button variant="primary" loading={busy} onClick={save}>
              {t("Сохранить")}
            </Button>
          </>
        )
      }
    >
      {!item && canEdit && <SmartFill tripId={tripId} want="item" day={v.day} onResult={onSmart} />}
      <fieldset disabled={!canEdit} className="grid gap-3">
        <div className="flex flex-wrap gap-1.5">
          {ITEM_KINDS.map((k) => (
            <button
              type="button"
              key={k}
              onClick={() => up({ kind: k })}
              className={cx(
                "flex h-9 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold",
                v.kind === k ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]" : "border-[var(--border)]",
              )}
            >
              {ITEM_KIND[k].emoji} {ITEM_KIND[k].label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("День")}>
            <Select value={v.day} onChange={(e) => up({ day: e.target.value })}>
              {allDays.map((x) => (
                <option key={x} value={x}>
                  {weekday(x)}, {fmtDayShort(x)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("Статус")}>
            <Select value={v.status ?? "planned"} onChange={(e) => up({ status: e.target.value as ItemStatus })}>
              {ITEM_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ITEM_STATUS[s].label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {v.kind !== "transport" && v.kind !== "note" && (
          <Field label={v.kind === "stay" ? t("Где ночуем (жильё или город)") : t("Место")}>
            {newPlace && !v.placeId ? (
              <div className="flex items-center gap-2 rounded-xl border border-dashed border-[var(--accent)] bg-[var(--accent-soft)] px-3 py-2 text-sm">
                <MapPinPlus size={16} className="shrink-0 text-[var(--accent)]" />
                <span className="min-w-0 flex-1">
                  {tj("{name} — новое место, появится на карте", { name: <b>{newPlace.name}</b> })}
                  <span className="block truncate text-[12px] text-muted">
                    {newPlace.address ?? newPlace.region ?? ""}
                    {newPlace.lat == null ? " · " + t("координаты не найдены") : ""}
                  </span>
                </span>
                <button type="button" onClick={() => setNewPlace(null)} aria-label={t("Убрать")} className="text-muted">
                  <X size={15} />
                </button>
              </div>
            ) : (
              <PlaceSelect
                places={placesArr}
                value={v.placeId ?? null}
                onChange={(p) => up({ placeId: p?.id ?? null, title: v.title || (p ? (v.kind === "stay" ? t("Ночёвка: {name}", { name: p.name }) : p.name) : v.title) })}
              />
            )}
          </Field>
        )}

        {v.kind === "transport" && (
          <>
            <Field label={t("Транспорт")}>
              <div className="flex flex-wrap gap-1.5">
                {TRANSPORT_MODES.map((m) => (
                  <button
                    type="button"
                    key={m}
                    onClick={() => up({ mode: m as TransportMode })}
                    className={cx(
                      "h-8 rounded-lg border px-2 text-[13px] font-semibold",
                      (v.mode ?? "bus") === m ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]" : "border-[var(--border)]",
                    )}
                  >
                    {MODE[m].emoji} {MODE[m].label}
                  </button>
                ))}
              </div>
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t("Откуда")}>
                <PlaceSelect
                  places={placesArr}
                  value={placesArr.find((p) => p.lat === v.fromLat && p.lng === v.fromLng)?.id ?? null}
                  onChange={(p) => up({ fromName: p?.name ?? null, fromLat: p?.lat ?? null, fromLng: p?.lng ?? null })}
                  placeholder={v.fromName ?? t("Откуда…")}
                />
              </Field>
              <Field label={t("Куда")}>
                <PlaceSelect
                  places={placesArr}
                  value={placesArr.find((p) => p.lat === v.toLat && p.lng === v.toLng)?.id ?? null}
                  onChange={(p) => up({ toName: p?.name ?? null, toLat: p?.lat ?? null, toLng: p?.lng ?? null })}
                  placeholder={v.toName ?? t("Куда…")}
                />
              </Field>
              <Field label={t("Отправление ({tz})", { tz: fromTz.split("/").pop()?.replace("_", " ") })}>
                <Input
                  type="datetime-local"
                  value={isoToLocalInput(v.departAt ?? null, fromTz)}
                  onChange={(e) => {
                    const iso = localInputToIso(e.target.value, fromTz);
                    // день пункта = местная дата отправления
                    up({ departAt: iso, ...(e.target.value ? { day: e.target.value.slice(0, 10), startTime: e.target.value.slice(11, 16) } : {}) });
                  }}
                />
              </Field>
              <Field label={t("Прибытие ({tz})", { tz: toTz.split("/").pop()?.replace("_", " ") })}>
                <Input type="datetime-local" value={isoToLocalInput(v.arriveAt ?? null, toTz)} onChange={(e) => up({ arriveAt: localInputToIso(e.target.value, toTz) })} />
              </Field>
              <Field label={t("Пояс отправления")}>
                <Select value={fromTz} onChange={(e) => up({ fromTz: e.target.value })}>
                  {[b.trip.destTz, b.trip.homeTz, "Asia/Bangkok", "Asia/Dubai", "Asia/Qatar", "Europe/Istanbul", "UTC"].filter((x, i, a) => a.indexOf(x) === i).map((z) => (
                    <option key={z}>{z}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t("Пояс прибытия")}>
                <Select value={toTz} onChange={(e) => up({ toTz: e.target.value })}>
                  {[b.trip.destTz, b.trip.homeTz, "Asia/Bangkok", "Asia/Dubai", "Asia/Qatar", "Europe/Istanbul", "UTC"].filter((x, i, a) => a.indexOf(x) === i).map((z) => (
                    <option key={z}>{z}</option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label={t("Перевозчик / рейс")}>
              <Input value={v.carrier ?? ""} onChange={(e) => up({ carrier: e.target.value })} placeholder={t("напр. Vietjet VJ123, автобус Bao Yen")} />
            </Field>
          </>
        )}

        <Field label={t("Название")}>
          <Input value={v.title ?? ""} onChange={(e) => up({ title: e.target.value })} placeholder={v.kind === "note" ? t("Текст заметки") : t("Что делаем")} />
        </Field>

        {v.kind !== "transport" && (
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("Начало")}>
              <Input type="time" value={v.startTime ?? ""} onChange={(e) => up({ startTime: e.target.value || null })} />
            </Field>
            <Field label={t("Конец")}>
              <Input type="time" value={v.endTime ?? ""} onChange={(e) => up({ endTime: e.target.value || null })} />
            </Field>
            {v.kind === "stay" ? (
              <Field label={t("Ночей")}>
                <Input type="number" min={1} max={60} value={v.nights ?? 1} onChange={(e) => up({ nights: Number(e.target.value) || 1 })} />
              </Field>
            ) : (
              <Field label={t("Длится, мин")}>
                <Input type="number" min={0} step={15} value={v.durationMin ?? ""} onChange={(e) => up({ durationMin: e.target.value ? Number(e.target.value) : null })} />
              </Field>
            )}
          </div>
        )}

        <Field label={t("Заметки")}>
          <Textarea value={v.notes ?? ""} onChange={(e) => up({ notes: e.target.value })} placeholder={t("Детали, советы, что взять")} />
        </Field>

        <details className="rounded-xl border border-[var(--border)] p-3" open={Boolean(v.bookingRef || v.cost || v.contact)}>
          <summary className="cursor-pointer text-sm font-semibold">{t("Бронь, деньги, контакты")}</summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label={t("Стоимость")}>
              <div className="flex gap-2">
                <Input type="number" min={0} step="any" value={v.cost ?? ""} onChange={(e) => up({ cost: e.target.value ? Number(e.target.value) : null })} />
                <Select className="w-24" value={v.currency ?? "EUR"} onChange={(e) => up({ currency: e.target.value })}>
                  {["EUR", "VND", "USD"].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </Select>
              </div>
            </Field>
            <Field label={t("Номер брони")}>
              <Input value={v.bookingRef ?? ""} onChange={(e) => up({ bookingRef: e.target.value })} />
            </Field>
            <Field label={t("Ссылка на бронь")}>
              <Input value={v.bookingUrl ?? ""} onChange={(e) => up({ bookingUrl: e.target.value })} placeholder="https://" />
            </Field>
            <Field label={t("Контакт (телефон, WhatsApp)")}>
              <Input value={v.contact ?? ""} onChange={(e) => up({ contact: e.target.value })} />
            </Field>
          </div>
        </details>

        {b.members.length > 1 && (
          <Field label={t("Кто участвует (пусто — вся группа)")}>
            <div className="flex flex-wrap gap-2">
              {b.members.map((m) => {
                const on = (v.participants ?? []).includes(m.id);
                return (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => up({ participants: on ? (v.participants ?? []).filter((x) => x !== m.id) : [...(v.participants ?? []), m.id] })}
                    className={cx("flex items-center gap-1.5 rounded-full border py-1 pr-3 pl-1 text-sm", on ? "border-[var(--accent)] bg-[var(--accent-soft)]" : "border-[var(--border)]")}
                  >
                    <Avatar m={m} size={22} /> {m.name ?? m.email}
                  </button>
                );
              })}
            </div>
          </Field>
        )}
      </fieldset>
    </Modal>
  );
}
